import React, { useCallback, useMemo, useRef, useState } from 'react'

import { Paginate, palette } from '@centivo/react-components'
import { useMap } from 'react-map-gl'

import { Spinner } from 'src/refactor/components'
import { usePagination } from 'src/refactor/hooks'
import { Content } from 'src/refactor/structure'
import { useMediaContext } from 'src/context/responsive'
import { Map } from 'src/refactor/blocks/Map'
import { useWatcher } from 'src/hooks'
import { useResultsState } from 'src/context/results'
import { ItemData } from 'src/context/results/types'

import { useSliceTemplate } from 'src/context/global'
import { ListItem } from './ListItem'
import {
  LoadingContainer,
  LoadingMessage,
  ResultsListFooter,
  RlListContainer,
  RlListMapContainer,
  RlMapContainer,
  RlPaginateContainer,
} from './ResultList.styled'
import { ResultsListHeader } from './ResultsListHeader'
import { ResultListMobile } from './ResultListMobile'
import { LoadingOverlay } from '../LoadingOverlay'
import { getBounds, getPracticeMarkers } from '../Map/functions'

const mapStyle: React.CSSProperties = {
  position: 'sticky',
  top: '0.5rem',
  width: '28.125rem',
  height: '30rem',
  maxWidth: '100%',
  maxHeight: '100%',
  marginBottom: '2rem',
}

export const ResultsList: React.FC = () => {
  const [state, actions] = useResultsState()
  const { desktop: map } = useMap()

  const { isMobileView } = useMediaContext()
  const [selectedPin, setSelectedPin] = useState<string | null>(null)

  const isSliceTemplate = useSliceTemplate()

  const list = useRef<HTMLDivElement>(null)
  const {
    paginatedElements: paginatedResults,
    pageCount,
    selectedPage,
    setSelectedPage,
    goToFirstPage,
  } = usePagination({
    elements: state.displayItems,
    pageSize: 10,
  })
  const pins = useMemo(
    () => getPracticeMarkers(paginatedResults, state.itemType, isSliceTemplate),
    [paginatedResults, state.itemType]
  )
  const isListEmpty = state.displayItems?.length === 0
  const shouldHideMap = !pins || pins.length === 0

  useWatcher(goToFirstPage, { items: state.displayItems })
  useWatcher(
    prev => {
      if (isMobileView && !state.isLoading && prev.isLoading) {
        list.current?.scrollIntoView({
          behavior: 'smooth',
        })
      }
    },
    { isLoading: state.isLoading }
  )
  useWatcher(
    () => {
      if (!isMobileView) {
        window.scrollTo(0, 0)
      }
    },
    { selectedPage },
    { shouldRunOnMount: false }
  )
  useWatcher(
    () => {
      const { bounds, fitBoundsOptions } = getBounds(
        pins.map(({ latitude, longitude }) => ({
          lat: latitude,
          long: longitude,
        }))
      )

      map?.fitBounds(bounds, fitBoundsOptions)
    },
    { pins },
    { shouldRunOnMount: false }
  )
  useWatcher(
    () => {
      if (selectedPin !== null) {
        const pinData = paginatedResults.find(({ id }) => id === selectedPin)

        if (pinData) {
          map?.setCenter({ lat: pinData.lat, lng: pinData.long })
          map?.setZoom(14)
        }
      }
    },
    { selectedPin }
  )

  const onPaginatorChange = ({ selected }: { selected: number }) => {
    setSelectedPage(selected)
  }

  const togglePopup = useCallback(
    (pin: string, forceState?: 'close' | 'open') => {
      const pinData = paginatedResults.find(({ id }) => id === pin)
      if (!pinData || pinData.isVirtualCareOnly) return

      if (forceState) {
        setSelectedPin(forceState === 'open' ? pin : null)
      } else {
        setSelectedPin(prev => (prev === pin ? null : pin))
      }
    },
    [setSelectedPin, paginatedResults]
  )

  const renderItem = useCallback(
    (data: ItemData) => (
      <ListItem
        id={data.id}
        key={data.id}
        data={data}
        type={data.itemType || state.itemType}
        isOpen={data.id === state.expandedPractice}
        onClickExpand={actions.setExpandedPractice}
        filterOptions={state.filter}
        specialtyCode={state.search.serviceLines}
        togglePopup={togglePopup}
      />
    ),
    [
      state.expandedPractice,
      state.filter,
      state.itemType,
      state.search.serviceLines,
      togglePopup,
    ]
  )

  const resultList = useMemo(
    () => paginatedResults.map(renderItem),
    [paginatedResults, renderItem]
  )

  const onClickCancelRequest = () => {
    actions.cancelRequest()
  }

  const { isRequestCanceled } = state

  if (state.isLoading) {
    return (
      <>
        <LoadingOverlay isOpen={isMobileView} />
        {!isMobileView && (
          <LoadingContainer>
            <Spinner
              color={palette.brand.orange}
              width={isSliceTemplate ? 8 : 5}
              size="64px"
              marginTop="2rem"
              centerVertically={false}
            />
            <>
              <span>Please wait.</span>
              {!!state.errorMsg && (
                <LoadingMessage>
                  <p>{state.errorMsg}</p>
                  <button onClick={onClickCancelRequest} type="button">
                    Cancel
                  </button>
                </LoadingMessage>
              )}
            </>
          </LoadingContainer>
        )}
      </>
    )
  }

  if (isListEmpty) {
    return (
      <Content>
        <RlListMapContainer
          isListEmpty={isListEmpty}
          isRequestCanceled={isRequestCanceled}
        >
          <ResultsListHeader
            hideText={isMobileView}
            setCurrentPage={setSelectedPage}
            isListEmpty={isListEmpty}
            isRequestCanceled={isRequestCanceled}
          />
        </RlListMapContainer>
      </Content>
    )
  }

  if (isMobileView) {
    return (
      <ResultListMobile
        ref={list}
        view={state.listView}
        isMobileView={isMobileView}
        setCurrentPage={setSelectedPage}
        selectedPin={selectedPin}
        setSelectedPin={setSelectedPin}
        specialtyCode={state.search.serviceLines}
        filterOptions={state.filter}
        data={state.displayItems}
        togglePopup={togglePopup}
        renderItem={renderItem}
      />
    )
  }

  const onMapRender = () => {
    const mapboxMap = document.querySelector('.mapboxgl-map')
    const elements = mapboxMap?.querySelectorAll('button, a, canvas')
    if (elements && elements.length > 0) {
      elements.forEach(el => {
        el.setAttribute('aria-hidden', 'true')
        el.setAttribute('tabIndex', '-1')
      })
    }
  }

  return (
    <Content className="result-list">
      <RlListMapContainer shouldHideMap={shouldHideMap}>
        <RlListContainer>
          <ResultsListHeader
            hideText={isMobileView}
            setCurrentPage={setSelectedPage}
            isListEmpty={isListEmpty}
          />
          {resultList}
        </RlListContainer>
        {!shouldHideMap ? (
          <RlMapContainer aria-hidden>
            <Map
              id="desktop"
              pins={pins}
              selectedPin={selectedPin}
              onSelectPin={togglePopup}
              style={mapStyle}
              onRender={onMapRender}
            />
          </RlMapContainer>
        ) : (
          <div className="spacer" />
        )}
        <ResultsListFooter>
          {(state.displayItems?.length ?? 0) > 10 && (
            <div className="pagination-container">
              <RlPaginateContainer>
                <Paginate
                  pageCount={pageCount}
                  forcePage={selectedPage}
                  onPageChange={onPaginatorChange}
                />
              </RlPaginateContainer>
            </div>
          )}
        </ResultsListFooter>
      </RlListMapContainer>
    </Content>
  )
}
